import { Form, FormikProvider, useFormik } from 'formik';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Flex } from 'reflexbox';

import { useErrorBoundary } from '@treams/treams-hooks';
import { Button, SettingsSection, Spinner, useToasts } from '@treams/treams-ui';

import DetailsTableForm from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import validator from '../../packages/kakapo-ui-services/factories/ValidatorFactory';
import { FieldValue, FormData } from './types';

type Props = {
	id?: string;
	formData: FormData;
	libraryPath: string;
};

const SettingsEdit: FC<Props> = ({ id, formData, libraryPath }) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const history = useHistory();

	const [, attempt, reset] = useErrorBoundary();
	const [create, { loading: createLoading }] = formData.create();
	const [update, { loading: updateLoading }] = formData.update();
	const queryResult =
		typeof formData.queryForSingle === 'function'
			? formData.queryForSingle({ variables: { id } })
			: formData.queryForSingle.query({ variables: { id, ...formData.queryForSingle.variables } });
	const loading = queryResult?.loading;
	const item = queryResult?.data?.[formData.subject.singular];

	const initialValues = formData.fields.reduce(
		(acc, curr) => {
			if (curr.type === DetailsTableFormValueTypes.CreateSelect) {
				acc[curr.name + 'ID'] = item ? item[curr.name].id : curr.value;
			} else {
				acc[curr.name] = item ? item[curr.name] : curr.value;
			}
			return acc;
		},
		{} as Record<string, FieldValue<any>>,
	);

	const validation = formData.fields.reduce(
		(acc, curr) => {
			if (curr.validation !== undefined) {
				if (curr.type === DetailsTableFormValueTypes.CreateSelect) {
					acc[curr.name + 'ID'] = curr.validation;
				} else {
					acc[curr.name] = curr.validation;
				}
			}
			return acc;
		},
		{} as Record<string, any>,
	);

	const formValues = formData.fields.map((field) => ({
		label: t(`common.settings.${formData.subject.plural}.edit.${field.name}`),
		name: field.type === DetailsTableFormValueTypes.CreateSelect ? `${field.name}ID` : field.name,
		type: field.type,
		options: field.options,
		fetchContent: field.fetchContent,
		createContent: field.createContent,
	}));

	const formik = useFormik({
		initialValues,
		onSubmit: () => onUpdate(),
		validationSchema: validator.object(validation),
	});

	useEffect(() => {
		if (loading) {
			return;
		}

		for (const [key, value] of Object.entries(initialValues)) {
			formik.setFieldValue(key, value, false);
		}
	}, [item]);

	const onUpdate = attempt(async () => {
		reset();

		if (item) {
			await update({
				variables: {
					input: {
						id,
						...formik.values,
					},
				},
			});
		} else {
			const { data } = await create({
				variables: {
					input: formik.values,
				},
			});

			if (data) {
				const capitalizedSingular =
					formData.subject.singular.charAt(0).toUpperCase() + formData.subject.singular.slice(1);

				history.replace(`${libraryPath}/${data[`create${capitalizedSingular}`].id}`);
			}
		}

		addToast(t('common.settings.goals.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	if (id && loading) {
		return <Spinner centered />;
	}

	return (
		<>
			<FormikProvider value={formik}>
				<Form>
					<SettingsSection first>
						<DetailsTableForm formValues={formValues} />
					</SettingsSection>
				</Form>
			</FormikProvider>

			<Flex mt={4} justifyContent="flex-end">
				<Button
					onClick={() => formik.submitForm()}
					loading={updateLoading || createLoading}
					title={t('common.save')}
				/>
			</Flex>
		</>
	);
};
export default SettingsEdit;
